import { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
}

type ParticleType = 'explosion' | 'trail' | 'float';

/**
 * Renders lightweight particle effects directly to a 2D canvas.
 *
 * Performance: particles live in a ref and are drawn inside a single
 * requestAnimationFrame loop. React state is NEVER updated per frame, so this
 * hook does not trigger re-renders while animating. The loop stops when idle.
 */
export const useParticleEffects = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const runningRef = useRef(false);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const stopLoop = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = 0;
    }
    runningRef.current = false;
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) {
      stopLoop();
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const particles = particlesRef.current;
    let write = 0;
    for (let read = 0; read < particles.length; read += 1) {
      const p = particles[read];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1;
      p.life -= 1;

      if (p.life > 0) {
        const opacity = p.life / p.maxLife;
        ctx.save();
        ctx.globalAlpha = Math.max(0, opacity);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        particles[write] = p;
        write += 1;
      }
    }
    particles.length = write;

    if (particles.length === 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stopLoop();
      return;
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [stopLoop]);

  const ensureLoop = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    animationRef.current = requestAnimationFrame(animate);
  }, [animate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctxRef.current = ctx;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth || canvas.width;
      canvas.height = canvas.offsetHeight || canvas.height;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      stopLoop();
      window.removeEventListener('resize', resizeCanvas);
      particlesRef.current = [];
      ctxRef.current = null;
    };
  }, [stopLoop]);

  const createParticles = useCallback(
    (x: number, y: number, count: number, color: string, type: ParticleType) => {
      const batch: Particle[] = [];
      for (let i = 0; i < count; i += 1) {
        batch.push({
          x,
          y,
          vx:
            type === 'explosion'
              ? (Math.random() - 0.5) * 4
              : (Math.random() - 0.5) * 2,
          vy:
            type === 'explosion'
              ? (Math.random() - 0.5) * 4
              : -Math.random() * 2,
          size: Math.random() * 3 + 2,
          color,
          life: type === 'explosion' ? 60 : 30,
          maxLife: type === 'explosion' ? 60 : 30,
        });
      }
      const particles = particlesRef.current;
      particles.push(...batch);
      if (particles.length > 400) {
        particles.splice(0, particles.length - 400);
      }
      ensureLoop();
    },
    [ensureLoop]
  );

  const createExplosion = useCallback(
    (x: number, y: number, color: string = '#00ff88') => {
      createParticles(x, y, 20, color, 'explosion');
    },
    [createParticles]
  );

  const createTrail = useCallback(
    (x: number, y: number, color: string = '#14b8a6') => {
      createParticles(x, y, 5, color, 'trail');
    },
    [createParticles]
  );

  const createFloatingParticles = useCallback(
    (x: number, y: number, color: string = '#7dffc2') => {
      createParticles(x, y, 10, color, 'float');
    },
    [createParticles]
  );

  return {
    canvasRef,
    createExplosion,
    createTrail,
    createFloatingParticles,
    getParticleCount: () => particlesRef.current.length,
  };
};
